import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  BriefingExportCard,
  EmptyState,
  ErrorState,
  FormField,
  KpiHeroBand,
  KpiBandSkeleton,
  ChartSkeleton,
  TableSkeleton,
  StatusDonutMini,
  WorkflowPipeline,
  buildPipelineSteps,
  StaggerReveal,
  PageHeader,
  Select,
  StatusChip,
  TextInput,
  toast,
  activityRail,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import './briefingProducts.css';
import './briefings.redesign.css';

// Canonical product lifecycle. needs_rework is rendered as a 'blocked' tone on
// the pending_review step rather than collapsing it back to a plain pending.
const LIFECYCLE_ORDER = ['draft', 'pending_review', 'approved_for_export', 'exported'];
const LIFECYCLE_LABELS = {
  draft: 'Draft',
  pending_review: 'In review',
  approved_for_export: 'Approved',
  exported: 'Exported',
};

function normalizeStatus(status) {
  const lower = String(status || 'draft').toLowerCase();
  if (lower === 'approved' || lower === 'humanreviewed' || lower === 'human_reviewed') {
    return 'approved_for_export';
  }
  if (lower === 'pendingreview') return 'pending_review';
  if (lower === 'generated') return 'approved_for_export';
  return lower;
}

// Map a product's current status onto the lifecycle pipeline. A needs_rework
// product is shown as blocked at the review gate (the stage it bounced from).
function pipelineForProduct(status) {
  const normalized = normalizeStatus(status);
  if (normalized === 'needs_rework') {
    return buildPipelineSteps({
      order: LIFECYCLE_ORDER,
      current: 'pending_review',
      statusByKey: { draft: 'done', pending_review: 'blocked' },
      labels: LIFECYCLE_LABELS,
    });
  }
  return buildPipelineSteps({
    order: LIFECYCLE_ORDER,
    current: normalized,
    labels: LIFECYCLE_LABELS,
  });
}

const EXPORT_STATUS_TONE = {
  completed: 'sage',
  exported: 'sage',
  done: 'sage',
  generated: 'sage',
  pending: 'amber',
  in_progress: 'amber',
  queued: 'amber',
  failed: 'brick',
  error: 'brick',
};

function exportStatusTone(status) {
  return EXPORT_STATUS_TONE[String(status || '').toLowerCase()] || 'blue';
}

const FORMAT_TONE = { pptx: 'amber', pdf: 'brick', docx: 'blue', html: 'sage', md: 'slate' };

function formatTone(format) {
  return FORMAT_TONE[String(format || '').toLowerCase()] || 'slate';
}

function fallbackMessage(record = {}) {
  if (record.fallback?.occurred !== true) return null;
  const requested = String(record.requestedFormat || 'requested format').toUpperCase();
  const actual = String(record.actualFormat || record.format || 'html').toUpperCase();
  const reason =
    {
      renderer_unavailable: 'PDF renderer unavailable',
      renderer_launch_failed: 'PDF renderer could not start',
      render_failed: 'PDF rendering failed',
      timeout: 'PDF rendering timed out',
      file_write_failed: 'PDF file could not be written',
      empty_output: 'PDF renderer produced an empty file',
      partial_output: 'PDF renderer produced an incomplete file',
      structural_validation_failed: 'PDF output failed validation',
      legacy_format_mismatch: 'legacy metadata did not match the stored file',
    }[record.fallback.reason] || 'requested format was unavailable';
  return `Delivered as ${actual} — ${requested} requested; ${reason}.`;
}

function unwrapEnvelope(payload) {
  return payload?.data?.data || payload?.data || payload;
}

// Resolve which export formats a product supports so the export card can offer a
// direct PPTX action for deck-type products. Prefer the product's own
// exportFormats, then fall back to its governing template's outputFormats
// (matched by templateId, then productType).
function resolveProductExportFormats(product, templates = []) {
  const own = Array.isArray(product?.exportFormats) ? product.exportFormats : [];
  if (own.length) return own;
  const template =
    templates.find((candidate) => candidate.id === product?.templateId) ||
    templates.find((candidate) => candidate.productType === product?.productType) ||
    null;
  return template?.outputFormats || template?.exportFormats || [];
}

function productTypeOptions() {
  return [
    { value: 'executive_deck', label: 'Executive deck' },
    { value: 'project_status', label: 'Project status brief' },
    { value: 'information_paper', label: 'Information paper' },
    { value: 'monthly_status_report', label: 'Monthly status report' },
    { value: 'closeout_package', label: 'Closeout package' },
    { value: 'final_project_report', label: 'Final project report' },
    { value: 'enhancement_implementation_report', label: 'Enhancement implementation report' },
    { value: 'uat_feedback_report', label: 'UAT feedback report' },
  ];
}

function humanizeList(values = []) {
  return values
    .map((value) => {
      const normalized = String(value || '')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();
      if (normalized === 'loes') return 'LOEs';
      if (normalized === 'ai runs') return 'AI runs';
      return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
    })
    .join(', ');
}

export function BriefingProductFactoryPage() {
  usePageMetadata({
    title: 'Briefing Product Factory',
    subtitle:
      'Generate governed executive products, route them through review, and preserve export history.',
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productsRes = useApi('/briefing-products');
  const templatesRes = useApi('/briefing-templates');
  const exportsRes = useApi('/exports');
  const capabilitiesRes = useApi('/briefing-export-capabilities');
  // Governed scope records for the search-picker (AC-PROD-1.1): scopes are
  // chosen from real records, never typed as raw IDs. Role-gated fetches that
  // fail simply leave their scope type without options.
  const portfoliosRes = useApi('/portfolios', { query: { limit: 100 } });
  const programsRes = useApi('/programs', { query: { limit: 100 } });
  const projectsRes = useApi('/projects', { query: { limit: 100 } });
  const loesRes = useApi('/loes');
  const uatSessionsRes = useApi('/uat/sessions', { query: { limit: 100 } });
  const intakeRes = useApi('/project-factory', { query: { limit: 100 } });
  const generateMut = useMutation();
  const reviewMut = useMutation();
  const exportMut = useMutation();
  const [productType, setProductType] = useState(
    () => searchParams.get('productType') || 'executive_deck',
  );
  const [scopeType, setScopeType] = useState(() => searchParams.get('scopeType') || 'loe');
  const [scopeId, setScopeId] = useState(() => searchParams.get('scopeId') || '');
  const [scopeSearch, setScopeSearch] = useState('');
  const [templateId, setTemplateId] = useState(() => searchParams.get('templateId') || '');
  const [timeframe, setTimeframe] = useState(() => searchParams.get('timeframe') || 'FY2026-Q4');
  // IMP-002: optional plain-language product title so tester/rehearsal decks can
  // be prefixed for findability. Empty = the template's auto-generated title.
  const [productTitle, setProductTitle] = useState(() => searchParams.get('title') || '');
  const [lastWarnings, setLastWarnings] = useState([]);
  const [generatedProducts, setGeneratedProducts] = useState([]);
  // Product pending the approve confirm + reviewer note (AC-PROD-2.1).
  const [pendingApproval, setPendingApproval] = useState(null);

  const requestedScopeId = searchParams.get('scopeId') || '';
  const requestedScopeType = searchParams.get('scopeType') || '';
  // AC-W7 — a scan-prefilled deep link carries the originating governed run id so the generated
  // product can cross-link back to its run. Absent (manual authoring), it stays null.
  const sourceAiRunId = searchParams.get('sourceAiRunId') || '';

  const scopeOptionsByType = useMemo(() => {
    // Live records don't share one id/name shape (LOEs carry loeId/title, not id/name).
    const asOption = (record) => {
      const id = record.id ?? record.loeId ?? record.code ?? '';
      return {
        id: String(id),
        label: String(record.name ?? record.title ?? id),
      };
    };
    // G7-APP-004: only records with a real stable ID are selectable scopes —
    // the empty value belongs to the placeholder alone (the LOE "Unassigned"
    // pseudo-row carries no id and must not masquerade as a governed record).
    const asOptions = (records = []) => {
      const seen = new Set();
      const options = [];
      for (const record of records) {
        const option = asOption(record);
        if (!option.id || seen.has(option.id)) continue;
        seen.add(option.id);
        options.push(option);
      }
      return options;
    };
    const uatCycles = [
      ...new Set(
        (uatSessionsRes.data?.data || [])
          .map((session) => session.cohort || session.cycle)
          .filter(Boolean),
      ),
    ];
    return {
      enterprise: [{ id: 'ENT-OWHA', label: 'OWHA Enterprise' }],
      portfolio: asOptions(portfoliosRes.data?.data),
      program: asOptions(programsRes.data?.data),
      project: asOptions(projectsRes.data?.data),
      loe: asOptions(loesRes.data?.data),
      uat_cycle: uatCycles.map((cycle) => ({ id: cycle, label: cycle })),
      intake_request: asOptions(intakeRes.data?.data),
    };
  }, [
    portfoliosRes.data,
    programsRes.data,
    projectsRes.data,
    loesRes.data,
    uatSessionsRes.data,
    intakeRes.data,
  ]);

  const scopeOptions = useMemo(() => {
    const base = scopeOptionsByType[scopeType] || [];
    // Preserve governed entry points that deep-link a scope this user's list
    // fetches don't include (embedded launches from scoped workspaces).
    const withRequested =
      requestedScopeId &&
      scopeId === requestedScopeId &&
      scopeType === requestedScopeType &&
      !base.some((o) => o.id === requestedScopeId)
        ? [{ id: requestedScopeId, label: requestedScopeId }, ...base]
        : base;
    const needle = scopeSearch.trim().toLowerCase();
    if (!needle) return withRequested;
    return withRequested.filter(
      (option) =>
        (option.label ?? '').toLowerCase().includes(needle) ||
        (option.id ?? '').toLowerCase().includes(needle),
    );
  }, [scopeOptionsByType, scopeType, scopeSearch, scopeId, requestedScopeId, requestedScopeType]);

  const scopeValid = Boolean(scopeId) && scopeOptions.some((option) => option.id === scopeId);

  // Changing scope type invalidates a previously picked record of another type.
  useEffect(() => {
    if (!scopeId) return;
    const base = scopeOptionsByType[scopeType] || [];
    const preserved =
      requestedScopeId && scopeId === requestedScopeId && scopeType === requestedScopeType;
    if (!preserved && !base.some((option) => option.id === scopeId)) {
      setScopeId('');
    }
  }, [scopeType, scopeOptionsByType, scopeId, requestedScopeId, requestedScopeType]);

  const templates = templatesRes.data?.data || [];
  const products = productsRes.data?.data || [];
  const exports = exportsRes.data?.data || [];
  const pdfCapability = capabilitiesRes.data?.data?.pdf || null;
  const pdfUnavailable = capabilitiesRes.error || pdfCapability?.available === false;
  const filteredTemplates = templates.filter(
    (template) => !productType || template.productType === productType,
  );
  const selectedTemplate =
    filteredTemplates.find((template) => template.id === templateId) ||
    filteredTemplates[0] ||
    null;
  const reviewQueue = [...generatedProducts, ...products].filter(
    (product, index, list) => list.findIndex((candidate) => candidate.id === product.id) === index,
  );

  // Review-queue rollups (derived purely from already-fetched products/exports).
  const queueByStatus = reviewQueue.reduce((acc, product) => {
    const key = normalizeStatus(product.status);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const pendingCount = (queueByStatus.pending_review || 0) + (queueByStatus.draft || 0);
  const reworkCount = queueByStatus.needs_rework || 0;
  const approvedCount = queueByStatus.approved_for_export || 0;
  const exportedCount =
    (queueByStatus.exported || 0) +
    exports.filter((record) => exportStatusTone(record.status) === 'sage').length;

  // Export-format mix from real export records.
  const exportFormatCounts = exports.reduce((acc, record) => {
    const fmt = String(record.format || 'unknown').toLowerCase();
    acc[fmt] = (acc[fmt] || 0) + 1;
    return acc;
  }, {});
  const formatSegments = Object.entries(exportFormatCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([fmt, count]) => ({ label: fmt.toUpperCase(), count, tone: formatTone(fmt) }));

  const kpiItems = [
    {
      id: 'queue-total',
      label: 'Products in pipeline',
      value: reviewQueue.length,
      status: reviewQueue.length ? 'blue' : 'slate',
      statusLabel: reviewQueue.length ? 'Active' : 'Empty',
      footer: `${approvedCount} approved · ${pendingCount} awaiting review`,
    },
    {
      id: 'awaiting-review',
      label: 'Awaiting review',
      value: pendingCount,
      status: pendingCount ? 'amber' : 'sage',
      statusLabel: pendingCount ? 'Pending' : 'Clear',
      footer: 'Draft + pending review',
    },
    {
      id: 'needs-rework',
      label: 'Needs rework',
      value: reworkCount,
      status: reworkCount ? 'brick' : 'sage',
      statusLabel: reworkCount ? 'Blocked' : 'None',
      footer: 'Returned by reviewer',
    },
    {
      id: 'exports',
      label: 'Completed exports',
      value: exportedCount,
      status: exportedCount ? 'sage' : 'slate',
      statusLabel: exportedCount ? 'Delivered' : 'None',
      footer: `${exports.length} export record${exports.length === 1 ? '' : 's'}`,
    },
  ];

  useEffect(() => {
    if (!filteredTemplates.length) {
      if (templateId) setTemplateId('');
      return;
    }
    if (!filteredTemplates.some((template) => template.id === templateId)) {
      setTemplateId(filteredTemplates[0].id);
    }
  }, [filteredTemplates, templateId]);

  async function generateDraft() {
    try {
      const trimmedTitle = productTitle.trim();
      const response = await generateMut.run(() =>
        api.post('/briefing-products/generate', {
          productType,
          templateId,
          scopeType,
          scopeId,
          timeframe,
          includeAiInsights: true,
          includeBudgetExecution: true,
          includeSourceAppendix: true,
          ...(trimmedTitle ? { title: trimmedTitle } : {}),
          ...(sourceAiRunId ? { sourceAiRunId } : {}),
        }),
      );
      const payload = unwrapEnvelope(response);
      const nextProduct = payload.product || {
        id: payload.briefingProductId,
        title: trimmedTitle || `${scopeType} ${scopeId}`,
        status: payload.status,
        productType,
        scopeType,
        scopeId,
        version: 1,
        citationCount: payload.citationCount || 0,
      };
      setGeneratedProducts((current) => [nextProduct, ...current]);
      setLastWarnings(payload.warnings || []);
      toast.success({
        title: 'Draft generated',
        body: `${payload.sectionsGenerated || 0} sections ready for review.`,
      });
      activityRail.push({
        action: 'briefing_product.generate',
        targetType: 'briefing_product',
        targetId: nextProduct.id,
        actorUserId: 'you',
        summary: nextProduct.title,
      });
    } catch (error) {
      toast.error({ title: 'Generation failed', body: error.message });
    }
  }

  // Approval requires the same explicit confirm + typed reviewer note as the
  // dossier (AC-PROD-2.1). The note is typed by the named human — never
  // boilerplate-autofilled.
  async function reviewProduct(product, note) {
    try {
      const response = await reviewMut.run(() =>
        api.post(`/briefing-products/${product.id}/review`, {
          action: 'approve_for_export',
          note,
        }),
      );
      const nextProduct = unwrapEnvelope(response);
      setGeneratedProducts((current) =>
        current.map((item) => (item.id === product.id ? { ...item, ...nextProduct } : item)),
      );
      setPendingApproval(null);
      toast.success({ title: 'Product approved', body: nextProduct.title || product.title });
      activityRail.push({
        action: 'briefing_product.approve_for_export',
        targetType: 'briefing_product',
        targetId: product.id,
        actorUserId: 'you',
        summary: product.title,
      });
      await productsRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Approval failed', body: error.message });
    }
  }

  async function exportProduct(product, format) {
    try {
      const response = await exportMut.run(() =>
        api.post(`/briefing-products/${product.id}/export`, { format }),
      );
      const payload = unwrapEnvelope(response);
      const exportRecord = payload.exportRecord || {};
      const actualFormat = exportRecord.actualFormat || exportRecord.format || format;
      const downloadUrl = exportRecord.downloadUrl || null;
      // Surface the returned artifact so the export is immediately reachable —
      // an "Open download" toast action plus the refreshed export-history row
      // (which renders the "Download ↗" link).
      toast.success({
        title: `${String(actualFormat).toUpperCase()} export created`,
        body: fallbackMessage(exportRecord) || exportRecord.fileName || product.title,
        action: downloadUrl
          ? {
              label: 'Open download',
              onClick: () => window.open(downloadUrl, '_blank', 'noopener,noreferrer'),
            }
          : undefined,
      });
      // Refresh so the new export appears in history and the card status advances.
      await Promise.all([productsRes.refresh?.(), exportsRes.refresh?.()]);
    } catch (error) {
      toast.error({ title: 'Export failed', body: error.message });
    }
  }

  if (productsRes.loading || templatesRes.loading || exportsRes.loading) {
    return (
      <div className="briefings-redesign">
        <PageHeader
          eyebrow="OWHA Products"
          title="Briefing Product Factory"
          subtitle="Generate decks, papers, reports, and closeout products from governed PRIME records."
        />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 'var(--space-4)' }}>
          <ChartSkeleton height={160} />
        </div>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <TableSkeleton rows={6} />
        </div>
      </div>
    );
  }
  if (productsRes.error) return <ErrorState error={productsRes.error} />;
  if (templatesRes.error) return <ErrorState error={templatesRes.error} />;
  if (exportsRes.error) return <ErrorState error={exportsRes.error} />;

  const headProduct = reviewQueue[0] || null;

  return (
    <div className="briefings-redesign">
      <PageHeader
        eyebrow="OWHA Products"
        title="Briefing Product Factory"
        subtitle="Generate decks, papers, reports, and closeout products from governed PRIME records."
      />

      <KpiHeroBand
        eyebrow="Pipeline posture"
        context={`${reviewQueue.length} in pipeline`}
        items={kpiItems}
        columns={4}
      />

      {pdfUnavailable ? (
        <p className="muted small" role="status">
          PDF unavailable in this environment — exports will be HTML.
        </p>
      ) : null}

      <section className="briefings-section briefing-factory-overview">
        <div className="card briefing-pipeline-card">
          <div className="eyebrow">Product lifecycle</div>
          <h2 className="card-title">{headProduct?.title || 'Governed product workflow'}</h2>
          <div className="briefing-pipeline-scroll">
            <WorkflowPipeline
              steps={pipelineForProduct(headProduct?.status || 'draft')}
              ariaLabel="Briefing product lifecycle"
            />
          </div>
          {headProduct?.status ? (
            <p className="muted small">
              Latest product is at the highlighted stage. Returned products surface as blocked at
              the review gate.
            </p>
          ) : (
            <p className="muted small">
              Draft to in-review to approved to exported. Returned products surface as blocked at
              the review gate.
            </p>
          )}
          <div className="briefing-pipeline-legend">
            <span>
              <span className="swatch done" /> Complete
            </span>
            <span>
              <span className="swatch active" /> In progress
            </span>
            <span>
              <span className="swatch blocked" /> Needs rework
            </span>
            <span>
              <span className="swatch pending" /> Pending
            </span>
          </div>
        </div>

        <div className="card briefing-mix-card">
          <div className="eyebrow">Export format mix</div>
          {formatSegments.length ? (
            <StatusDonutMini
              segments={formatSegments}
              size={120}
              totalLabel="Exports"
              ariaLabel="Export format mix"
            />
          ) : (
            <p className="muted small">
              No exports recorded yet. Approved products will populate the format mix.
            </p>
          )}
        </div>
      </section>

      <section className="briefing-product-grid">
        <form
          className="card briefing-product-form"
          onSubmit={(event) => {
            event.preventDefault();
            generateDraft();
          }}
        >
          <header>
            <div className="eyebrow">Generate draft</div>
            <h2 className="card-title">Product factory controls</h2>
          </header>
          <FormField label="Product type" htmlFor="product-type">
            <Select
              id="product-type"
              value={productType}
              onChange={setProductType}
              options={productTypeOptions()}
            />
          </FormField>
          <FormField label="Scope type" htmlFor="scope-type">
            <Select
              id="scope-type"
              value={scopeType}
              onChange={setScopeType}
              options={[
                { value: 'enterprise', label: 'Enterprise' },
                { value: 'uat_cycle', label: 'UAT cycle' },
                { value: 'loe', label: 'LOE' },
                { value: 'portfolio', label: 'Portfolio' },
                { value: 'program', label: 'Program' },
                { value: 'project', label: 'Project' },
                { value: 'intake_request', label: 'Intake request' },
              ]}
            />
          </FormField>
          <FormField
            label="Find a scope"
            htmlFor="scope-search"
            hint="Filter the governed records below by name or ID."
          >
            <TextInput
              id="scope-search"
              value={scopeSearch}
              onChange={setScopeSearch}
              placeholder="Start typing to filter…"
            />
          </FormField>
          <FormField
            label="Scope record"
            htmlFor="scope-record"
            hint={
              scopeOptions.length
                ? 'Products are generated from governed records — pick one, no raw IDs.'
                : 'No governed records available for this scope type.'
            }
          >
            <Select
              id="scope-record"
              value={scopeValid ? scopeId : ''}
              onChange={setScopeId}
              options={[
                {
                  value: '',
                  label: scopeOptions.length ? 'Select a record…' : 'No records available',
                },
                ...scopeOptions.map((option) => ({ value: option.id, label: option.label })),
              ]}
            />
          </FormField>
          <FormField label="Template" htmlFor="template-id">
            <Select
              id="template-id"
              value={templateId}
              onChange={setTemplateId}
              options={filteredTemplates.map((template) => ({
                value: template.id,
                label: template.name,
              }))}
            />
          </FormField>
          <FormField label="Timeframe" htmlFor="timeframe">
            <TextInput id="timeframe" value={timeframe} onChange={setTimeframe} />
          </FormField>
          {/* IMP-002: optional product title. Defaults to the template's
              auto-generated title; lets a user prefix or rename the product
              (e.g. a "UAT Rehearsal" deck) for findability. */}
          <FormField
            label="Product title (optional)"
            htmlFor="product-title"
            hint="Leave blank to use the template's auto-generated title."
          >
            <TextInput
              id="product-title"
              value={productTitle}
              onChange={setProductTitle}
              placeholder="e.g. UAT Rehearsal — OWHA Director Deck"
            />
          </FormField>
          <div className="briefing-source-preview">
            <div className="eyebrow">Source preview</div>
            <p>
              Scope: <strong>{scopeType}</strong> {scopeId || 'not selected'}
            </p>
            <p className="muted small">
              AI insights, budget execution, and source appendix will be included in this draft.
            </p>
            {selectedTemplate ? (
              <div className="stack tight">
                <p>
                  Template: <strong>{selectedTemplate.name}</strong>
                </p>
                {selectedTemplate.description ? (
                  <p className="muted small">{selectedTemplate.description}</p>
                ) : null}
                <p className="muted small">
                  Data sources:{' '}
                  {selectedTemplate.requiredDataSources?.length
                    ? humanizeList(selectedTemplate.requiredDataSources)
                    : 'Governed PRIME records'}
                </p>
                <p className="muted small">
                  Output formats:{' '}
                  {(selectedTemplate.outputFormats || selectedTemplate.exportFormats || ['pdf'])
                    .filter((format) => String(format).toLowerCase() !== 'pdf' || !pdfUnavailable)
                    .map((format) => String(format || '').toUpperCase())
                    .join(', ')}
                </p>
                <p className="muted small">
                  Review:{' '}
                  {selectedTemplate.humanReviewRequired === false
                    ? 'Direct export allowed'
                    : 'Human review required'}
                </p>
                <p className="muted small">
                  AI policy:{' '}
                  {selectedTemplate.aiAllowed === false
                    ? 'AI-authored insights disabled'
                    : 'AI draft assistance enabled'}
                </p>
              </div>
            ) : null}
          </div>
          {lastWarnings.length ? (
            <div className="briefing-warning-stack">
              {lastWarnings.map((warning) => (
                <article key={warning} className="briefing-warning-card">
                  <div className="eyebrow">Data freshness warning</div>
                  <p>{warning}</p>
                </article>
              ))}
            </div>
          ) : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            {/* AC-PROD-1.1/1.2: invalid scopes cannot be submitted; generation
                disables while a draft is in flight (no double submit). */}
            <button
              className="btn primary tactile-press"
              type="submit"
              disabled={!templateId || !scopeValid || generateMut.running}
              title={
                scopeValid ? undefined : 'Pick a governed scope record before generating a draft.'
              }
            >
              {generateMut.running ? 'Generating…' : 'Generate draft'}
            </button>
          </div>
        </form>

        <div className="card briefing-product-summary">
          <header className="row spread">
            <div>
              <div className="eyebrow">Review queue</div>
              <h2 className="card-title">Pending and recent products</h2>
            </div>
            <StatusChip status={reviewQueue[0]?.status || 'pending_review'} />
          </header>
          {reviewQueue[0]?.status ? (
            <div className="muted small mono">{reviewQueue[0].status}</div>
          ) : null}
          {reviewQueue.length === 0 ? (
            <EmptyState
              title="No briefing products yet"
              description="Generate a product draft to populate the review queue."
            />
          ) : (
            <div className={`briefing-grid${pdfUnavailable ? ' briefing-pdf-unavailable' : ''}`}>
              {pdfUnavailable ? (
                <style>{`.briefing-pdf-unavailable .briefing-actions > button:nth-of-type(3) { display: none; }`}</style>
              ) : null}
              {reviewQueue.map((product) => {
                // Resolve the product's export formats so deck products expose a
                // direct PPTX action on the card (mirrors the product detail page).
                const productExportFormats = resolveProductExportFormats(product, templates).filter(
                  (format) => String(format).toLowerCase() !== 'pdf' || !pdfUnavailable,
                );
                return (
                  <BriefingExportCard
                    key={product.id}
                    briefing={{
                      ...product,
                      exportFormats: productExportFormats,
                      format: productExportFormats[0] || product.format || 'pptx',
                    }}
                    onOpen={() => navigate(`/briefings/${product.id}`)}
                    onApprove={(item) => setPendingApproval(item)}
                    onExportHtml={(item) => exportProduct(item, 'html')}
                    onExportPdf={pdfUnavailable ? undefined : (item) => exportProduct(item, 'pdf')}
                    onExportPptx={(item) => exportProduct(item, 'pptx')}
                    busy={reviewMut.running || exportMut.running}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="card briefing-export-history-card briefings-section">
        <header className="briefings-section-head">
          <div>
            <div className="eyebrow">Export history</div>
            <h2 className="card-title">Completed exports</h2>
          </div>
          {formatSegments.length ? (
            <span className="chip outline sm">
              {exports.length} export{exports.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </header>
        {exports.length === 0 ? (
          <EmptyState
            title="No exports yet"
            description="Approved products will add PPTX, PDF, and DOCX history here."
          />
        ) : (
          <StaggerReveal className="briefing-export-bento">
            {exports.map((exportRecord) => {
              const tone = exportStatusTone(exportRecord.status);
              return (
                <article key={exportRecord.id} className={`briefing-export-panel tone-${tone}`}>
                  <div className="briefing-export-panel__head">
                    <span className="briefing-export-panel__fmt">
                      {String(exportRecord.actualFormat || exportRecord.format || '').toUpperCase()}
                    </span>
                    <StatusChip status={exportRecord.status} />
                  </div>
                  <div className="briefing-export-panel__file">{exportRecord.fileName}</div>
                  {fallbackMessage(exportRecord) ? (
                    <p className="muted small">{fallbackMessage(exportRecord)}</p>
                  ) : null}
                  <div className="briefing-export-panel__meta">
                    <span className="mono">{exportRecord.briefingProductId}</span>
                    <span>v{exportRecord.version || 1}</span>
                  </div>
                  {/* AC-PROD-1.2: export rows carry timestamps + download links. */}
                  <div className="briefing-export-panel__meta">
                    {exportRecord.generatedAt ? (
                      <span>{new Date(exportRecord.generatedAt).toLocaleString()}</span>
                    ) : null}
                    {exportRecord.downloadUrl ? (
                      <a
                        className="btn link sm"
                        href={exportRecord.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download ↗
                      </a>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </StaggerReveal>
        )}
      </section>

      {/* AC-PROD-2.1: approve here uses the same confirm + typed reviewer note
          as the dossier — the export gate is only ever opened by a named human. */}
      <ConfirmActionDialog
        open={Boolean(pendingApproval)}
        title="Approve this product for export?"
        body={
          pendingApproval
            ? `Approving "${pendingApproval.title || pendingApproval.id}" records you as the named human reviewer and unlocks its export controls.`
            : ''
        }
        confirmLabel="Approve for export"
        requireNote
        noteLabel="Reviewer note"
        notePlaceholder="What did you verify before approving?"
        busy={reviewMut.running}
        onConfirm={(note) => pendingApproval && reviewProduct(pendingApproval, note)}
        onCancel={() => setPendingApproval(null)}
      />
    </div>
  );
}
